import { useState, useRef, useEffect } from 'react';
import { useCredit } from '../../../config/creditContext';
import axios from 'axios';

const SUPPORTED_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/**
 * Custom hook for file upload logic.
 * @param {Object} params
 * @param {string} params.HOST - Upload server host.
 * @param {function} params.ensureAuthenticated - Function to check authentication.
 * @param {function} params.onUploadSuccess - Callback on successful upload.
 * @param {function} params.toast - Toast notification function/object.
 * @returns {Object}
 */
export default function useFileUpload({
  HOST,
  ensureAuthenticated,
  onUploadSuccess,
  toast,
}) {
  const { userId } = useCredit();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [filePath, setFilePath] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Cleanup upload state on unmount
  useEffect(() => {
    return () => {
      setUploadedFile(null);
      setUploadProgress(null);
      setFilePath(null);
      setDragging(false);
    };
  }, []);

  const handleFileChange = async (file) => {
    if (!ensureAuthenticated()) {
      return;
    }
    if (file) {
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        toast?.error?.(
          'Only PDF, DOCX, DOC, JPG, JPEG and PNG files are allowed.'
        ) || (typeof toast === 'function' && toast('Only PDF, DOCX, DOC, JPG, JPEG and PNG files are allowed.'));
        return;
      }
      setUploadedFile(file);
      setUploadProgress('loading');
      const formData = new FormData();
      formData.append('file', file);

      // Add user_id if available
      if (userId) {
        formData.append('user_id', userId);
      }

      try {
        const response = await fetch(`${HOST}/upload`, {
          method: 'POST',
          body: formData,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });
        if (response.status === 503) {
          toast?.error?.('Server is currently busy. Please try again later.') || (typeof toast === 'function' && toast('Server is currently busy. Please try again later.'));
          setUploadProgress(null);
          return;
        }
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Upload failed with status:', response.status, 'Error:', errorText);
          throw new Error(`Upload failed: ${response.status} ${errorText}`);
        }
        const result = await response.json();
        if (result && result.saved_path) {
          setFilePath(result.saved_path);
          setUploadProgress('success');

          // Store doc_id in sessionStorage for tracking throughout the flow
          if (result.doc_id) {
            sessionStorage.setItem('doc_id', result.doc_id);
            console.log('Stored doc_id in sessionStorage:', result.doc_id);
          }

          // Update analytics from frontend
          try {
            if (userId) {
              await axios.put(`${process.env.REACT_APP_DB_SERVER_URL}/api/users-analytics/update-attribute`, {
                user_id: userId,
                attribute: "total_document_uploaded"
              });
            }
          } catch (error) {
            console.warn('Failed to update upload analytics:', error);
          }

          toast?.success?.('File check cleared. Select translation preferences and continue...') || (typeof toast === 'function' && toast('File check cleared. Select translation preferences and continue...'));
          onUploadSuccess?.();
        } else {
          setUploadProgress(null);
          toast?.error?.(result?.error || 'File upload failed: Invalid response from server.') || (typeof toast === 'function' && toast(result?.error || 'File upload failed: Invalid response from server.'));
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(null);
        toast?.error?.('File upload failed.') || (typeof toast === 'function' && toast('File upload failed.'));
      }
    }
  };

  const handleFileInputChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      handleFileChange(event.target.files[0]);
    }
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    setUploadProgress(null);
    setFilePath(null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!ensureAuthenticated()) {
      return;
    }
    const file = event.dataTransfer.files[0];
    handleFileChange(file);
  };

  return {
    uploadedFile,
    uploadProgress,
    filePath,
    dragging,
    fileInputRef,
    SUPPORTED_FORMATS,
    handleFileChange,
    handleFileInputChange,
    handleDeleteFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    setUploadedFile,
    setUploadProgress,
    setFilePath,
    setDragging,
  };
} 